/************************************************************************************

* [MODULE]: PRODUCT INFORMATION
  -- Main features:
  + l-product-form
  + l-variant-selects
  + l-product-controls
  + l-product-quantity-input

*************************************************************************************/
if (!customElements.get('l-product-info')) {
	customElements.define(
		'l-product-info',
		class LProductInfo extends HTMLElement {
			quantityInput = undefined
			quantityForm = undefined
			abortController = undefined
			pendingRequestUrl = null
			preProcessHtmlCallbacks = []
			postProcessHtmlCallbacks = []

			constructor() {
				super()
			}

			connectedCallback() {
				// Initialize the buttons state
				this.initializeProductSwapUtility()

				// Data
				this.selectedSellingPlanId = this.dataset.sellingPlanId

				// Dispatch events
				this.dispatchEvent(new CustomEvent('l:product-info:loaded', { bubbles: true }))

				// Catch variant picker options changed events
				document.addEventListener('l:variant:option-changed', (event) => {
					const { target, selectedOptionValues } = event.detail
					if (!this.contains(target)) return

					this.handleOptionValueChange(target, selectedOptionValues, null)
				})

				document.addEventListener('l:trigger-variant-change', (event) => {
					const { target, selectedOptionValues, variant } = event.detail

					this.handleOptionValueChange(target, selectedOptionValues, variant)
				})

				// Catch selling plan changed events
				document.addEventListener('l:sellingPlan:changed', (event) => {
					const { sellingPlanId } = event.detail
					const variant = this.getSelectedVariant(this)
					this.selectedSellingPlanId = sellingPlanId

					this.handleOptionValueChange(event.target, null, variant)
				})
			}

			addPreProcessCallback(callback) {
				this.preProcessHtmlCallbacks.push(callback)
			}

			initializeProductSwapUtility() {
				this.postProcessHtmlCallbacks.push((newNode) => {
					window?.Shopify?.PaymentButton?.init()
					window?.ProductModel?.loadShopifyXR()
				})
			}

			handleOptionValueChange(target, selectedOptionValues, variant) {
				// Reset form data
				this.resetProductFormState()

				// Check new selected status / URLs
				const productUrl = target?.dataset?.productUrl || this.pendingRequestUrl || this.dataset.url

				this.pendingRequestUrl = productUrl
				const shouldSwapProduct = this.dataset.url !== productUrl
				const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct

				// Render product information based on new selected options
				this.renderProductInfo({
					requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, variant, shouldFetchFullPage),
					targetId: target?.id,
					callback: shouldSwapProduct
						? this.handleSwapProduct(productUrl, shouldFetchFullPage)
						: this.handleUpdateProductInfo(productUrl),
				})
			}

			resetProductFormState() {
				if (this.productForm) {
					this.productForm.global_toggleSubmitButton(true, null, false)
					this.productForm.handleErrorMessage()
				}
			}

			renderProductInfo({ requestUrl, targetId, callback }) {
				this.abortController?.abort()
				this.abortController = new AbortController()

				fetch(requestUrl, { signal: this.abortController.signal })
					.then((response) => response.text())
					.then((responseText) => {
						this.pendingRequestUrl = null
						const html = new DOMParser().parseFromString(responseText, 'text/html')

						callback(html)
					})
					.then(() => {
						// set focus to last clicked option value
						document.querySelector(`#${targetId}`)?.focus()
					})
					.catch((error) => {
						console.error(error)
					})
			}

			handleSwapProduct(productUrl, updateFullPage) {
				return (html) => {
					const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info'
					const variant = this.getSelectedVariant(html.querySelector(selector))
					this.updateURL(productUrl, variant?.id)

					if (updateFullPage) {
						document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML

						LTA_HTMLUpdateUtility.viewTransition(
							document.querySelector('main'),
							html.querySelector('main'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					} else {
						LTA_HTMLUpdateUtility.viewTransition(
							this,
							html.querySelector('product-info'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					}
				}
			}

			handleUpdateProductInfo(productUrl) {
				return (html) => {
					const variant = this.getSelectedVariant(html)

					this.updateOptionValues(html)
					this.updateURL(productUrl, variant?.id)
					this.updateVariantInputs(variant?.id)

					if (!variant) {
						this.setUnavailable()

						return
					}

					const updateSourceFromDestination = (id, htmlMethod = 'innerHTML', shouldHide = (source) => false) => {
						const source = html.getElementById(`${id}-${this.sectionId}`)
						const destination = document.getElementById(`${id}-${this.dataset.section}`)

						if (source && destination) {
							destination[htmlMethod] = source[htmlMethod]
							destination.classList.toggle('l-hidden', shouldHide(source))
						}
					}

					updateSourceFromDestination('price', 'outerHTML')
					updateSourceFromDestination('stockInfo', 'outerHTML')
					updateSourceFromDestination('Subscriptions', 'outerHTML')
					updateSourceFromDestination('ProductSubmitButton')

					updateSourceFromDestination('stickyATCPrices', 'outerHTML')
					updateSourceFromDestination('stickyATCWrapper', 'outerHTML')

					this.productForm?.global_toggleSubmitButton(
						html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
						'soldOut'
					)

					document.dispatchEvent(
						new CustomEvent('l:variant:changed', { detail: { sectionId: this.sectionId, html, variant } })
					)
				}
			}

			updateOptionValues(html) {
				const variantSelects = html.querySelector('l-variant-selects')

				if (variantSelects) {
					LTA_HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks)
				}
			}

			updateURL(url, variantId) {
				if (this.dataset.updateUrl === 'false') return

				const params = new URLSearchParams()
				if (variantId) params.append('variant', variantId)
				if (this.selectedSellingPlanId) params.append('selling_plan', this.selectedSellingPlanId)
				const newUrl = `${url}?${params.toString()}`

				window.history.replaceState({}, '', newUrl)
			}

			updateVariantInputs(variantId) {
				this.querySelectorAll(`#product-form-${this.dataset.section}`).forEach((productForm) => {
					const input = productForm.querySelector('input[name="id"]')
					input.value = variantId ?? ''
					input.dispatchEvent(new Event('change', { bubbles: true }))
				})
			}

			setUnavailable() {
				this.productForm?.global_toggleSubmitButton(true, 'unavailable')

				const selectors = ['price', 'stockInfo', 'Subscriptions', 'stickyATC']
					.map((id) => `#${id}-${this.dataset.section}`)
					.join(', ')

				document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('l-hidden'))
			}

			getSelectedVariant(productInfoNode) {
				const selectedVariant = productInfoNode.querySelector('l-variant-selects [data-selected-variant]')?.innerHTML
				return !!selectedVariant ? JSON.parse(selectedVariant) : null
			}

			buildRequestUrlWithParams(url, optionValues, variant, shouldFetchFullPage = false) {
				const params = []

				!shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)

				if (this.selectedSellingPlanId) params.push(`selling_plan=${this.selectedSellingPlanId}`)

				if (optionValues?.length) {
					params.push(`option_values=${optionValues.join(',')}`)
				} else if (variant) {
					params.push(`variant=${variant.id}`)
				}

				return `${url}?${params.join('&')}`
			}

			get productForm() {
				return this.querySelector('l-product-form')
			}

			get variantSelectors() {
				return this.querySelector('l-variant-selects')
			}

			get sectionId() {
				return this.dataset.originalSection || this.dataset.section
			}
		}
	)
}

if (!customElements.get('l-product-form')) {
	customElements.define(
		'l-product-form',
		class LTAProductForm extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.form = this.querySelector('form') || document.getElementById(this.dataset.formId)
				this.cart = document.getElementById('CartDrawer')
				this.submitButton = this.querySelector('[type="submit"]')
				this.submitButtonText = this.submitButton.querySelector('[data-add-to-cart-label]')

				// Set data / attribute
				this.sectionId = this.dataset.sectionId
				this.hideErrors = this.dataset.hideErrors === 'true'

				// Setup event
				this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
			}

			onSubmitHandler(evt) {
				evt.preventDefault()

				if (this.submitButton.getAttribute('aria-disabled') === 'true') return

				// Setup button loading state
				this.submitButton.setAttribute('aria-disabled', true)
				this.submitButton.classList.add('is-loading')

				// Setup adding product to cart
				const config = L_ATC.handleConfigWithFormEl(this.form)

				L_ATC.addToCart(config, 'l-product-form')
					.catch((e) => {
						console.error(e)
					})
					.finally(() => {
						// Remove button loading state
						this.submitButton.classList.remove('is-loading')
						if (!this.error) this.submitButton.removeAttribute('aria-disabled')
					})
			}

			handleErrorMessage(errorMessage = false) {
				if (this.hideErrors) return

				if (!this.errorMessageWrapper) {
					this.errorMessageWrapper = document.getElementById(`ProductErrorMessages-${this.sectionId}`)
					this.errorMessage = this.errorMessageWrapper.querySelector('[data-error-message]')
				}

				if (!this.errorMessageWrapper) return

				this.errorMessageWrapper.classList.toggle('l-hidden', !errorMessage)
				if (errorMessage) this.errorMessage.textContent = errorMessage
			}

			global_toggleSubmitButton(disable = true, textType, needToUpdateText = true) {
				if (disable) {
					this.submitButton.setAttribute('disabled', 'disabled')
					if (textType) this.submitButtonText.textContent = this.getButtonLabel(textType)

					return
				}

				this.submitButton.removeAttribute('disabled')

				if (needToUpdateText) {
					this.submitButtonText.textContent = this.getButtonLabel('addToCart')
				}
			}

			getButtonLabel(textType = 'addToCart') {
				switch (textType) {
					case 'addToCart':
						return this.dataset.atcLabel || window.ltatheme.variantStrings.addToCart
					case 'soldOut':
						return this.dataset.soldOutLabel || window.ltatheme.variantStrings.soldOut
					case 'preOrder':
						return this.dataset.preOrderLabel || window.ltatheme.variantStrings.preOrder
					case 'unavailable':
						return this.dataset.unavailableLabel || window.ltatheme.variantStrings.unavailable
					default:
						return window.ltatheme.variantStrings.addToCart
				}
			}

			get variantIdInput() {
				return this.form.querySelector('[name=id]')
			}
		}
	)
}

if (!customElements.get('l-variant-selects')) {
	customElements.define(
		'l-variant-selects',
		class LTAVariantSelect extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.addEventListener('change', (event) => {
					const target = this.getInputForEventTarget(event.target)

					this.updateSelectionMetadata(event)

					document.dispatchEvent(
						new CustomEvent('l:variant:option-changed', {
							detail: {
								event,
								target,
								selectedOptionValues: this.selectedOptionValues,
							},
						})
					)
				})
			}

			updateSelectionMetadata({ target }) {
				const { value, tagName } = target

				if (tagName === 'SELECT' && target.selectedOptions.length) {
					return
				}

				// Update selected option value
				if (target.hasAttribute('data-product-variant-input')) {
					const selectedSwatchValue = target.closest('[data-variant-options]').querySelector('[data-selected-value]')
					if (selectedSwatchValue) selectedSwatchValue.innerHTML = value
				}
			}

			getInputForEventTarget(target) {
				return target.tagName === 'SELECT' ? target.selectedOptions[0] : target
			}

			get selectedOptionValues() {
				return Array.from(
					this.querySelectorAll('[data-variant-select] option[selected], [data-product-variant-input]:checked')
				).map(({ dataset }) => dataset.optionValueId)
			}
		}
	)
}

if (!customElements.get('l-product-quantity-input')) {
	customElements.define(
		'l-product-quantity-input',
		class LTAProductQuantityInput extends HTMLElement {
			constructor() {
				super()
			}

			quantityUpdateUnsubscriber = undefined

			connectedCallback() {
				// Elements
				this.input = this.querySelector('input')

				// Setup events
				this.changeEvent = new Event('change', { bubbles: true })

				this.input.addEventListener('change', () => {
					this.validateQtyRules()
				})

				this.querySelectorAll('button').forEach((button) =>
					button.addEventListener('click', this.onButtonClick.bind(this))
				)

				// Subscribe global event
				document.addEventListener('l:quantity:update', this.validateQtyRules.bind(this))

				// Validate Quantity Rules
				this.validateQtyRules()
			}

			onButtonClick(event) {
				event.preventDefault()

				const previousValue = this.input.value

				if (event.target.name === 'plus') {
					// Reset value to minimum value || increase value based on step
					const needToResetMinimumValue =
						parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0

					needToResetMinimumValue ? (this.input.value = this.input.dataset.min) : this.input.stepUp()
				} else {
					this.input.stepDown()
				}

				// Dispatch event
				if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent)

				// Reset to minimum value
				if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
					this.input.value = parseInt(this.input.min)
				}
			}

			validateQtyRules() {
				const value = parseInt(this.input.value)

				if (this.input.min) {
					const buttonMinus = this.querySelector("button[name='minus']")
					buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min))
				}

				if (this.input.max) {
					const max = parseInt(this.input.max)
					const buttonPlus = this.querySelector("button[name='plus']")
					buttonPlus.classList.toggle('disabled', value >= max)
				}
			}
		}
	)
}

if (!customElements.get('l-product-controls')) {
	customElements.define(
		'l-product-controls',
		class LTAProductInfo extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.quantityFormEl = this.querySelector('l-product-quantity-input')
				this.quantityInputEl = this.quantityFormEl?.querySelector('input')
				this.productForm = this.querySelector('l-product-form')

				if (!this.quantityInputEl || !this.quantityFormEl) return

				// Subscribe global event
				document.addEventListener('l:variant:changed', this.updateQuantityRules.bind(this))

				// Setup validate quantity controls
				this.setQuantityBoundries()
			}

			setQuantityBoundries() {
				// Get data from l-product-quantity-input element
				const quantityDataset = this.quantityInputEl.dataset
				const data = {
					cartQuantity: quantityDataset.cartQuantity ? parseInt(quantityDataset.cartQuantity) : 0,
					min: quantityDataset.min ? parseInt(quantityDataset.min) : 1,
					max: quantityDataset.max ? parseInt(quantityDataset.max) : null,
					step: this.quantityInputEl.step ? parseInt(this.quantityInputEl.step) : 1,
				}

				// Get min-max quantity of selected variant based on variant in current cart
				let min = data.min
				const max = data.max === null ? data.max : data.max - data.cartQuantity
				if (max !== null) min = Math.min(min, max)
				if (data.cartQuantity >= data.min) min = Math.min(min, data.step)

				// Set values for quantity input element (min, max, value)
				this.quantityInputEl.min = min
				this.quantityInputEl.value = min
				max ? (this.quantityInputEl.max = max) : this.quantityInputEl.removeAttribute('max')

				// Publish quantity update
				document.dispatchEvent(new CustomEvent('l:quantity:update', { detail: { id: this.dataset.section } }))
			}

			updateQuantityRules(event) {
				const { html, sectionId } = event.detail

				const quantityFormUpdated = html.getElementById(`Product-Form-Controls-${sectionId}`)
				const selectors = ['[data-product-quantity-input]', '[data-quantity-label]']

				for (let selector of selectors) {
					const current = this.querySelector(selector)
					const updated = quantityFormUpdated.querySelector(selector)
					if (!current || !updated) continue

					// Update product quantity input || other elements
					if (selector === '[data-product-quantity-input]') {
						const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step']
						for (let attribute of attributes) {
							const valueUpdated = updated.getAttribute(attribute)
							valueUpdated !== null ? current.setAttribute(attribute, valueUpdated) : current.removeAttribute(attribute)
						}
					} else {
						current.innerHTML = updated.innerHTML
					}
				}

				// Update and dispatch quantity changed event
				this.setQuantityBoundries()
			}

			// b2b__fetchQuantityRules() {
			// 	// Get current selected variant ID
			// 	const currentVariantId = this.productForm?.variantIdInput?.value
			// 	if (!currentVariantId) return

			// 	// Call API to update rules cart component
			// 	this.querySelector('[data-rules-cart]').classList.add('is-loading')

			// 	fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
			// 		.then((response) => response.text())
			// 		.then((responseText) => {
			// 			const html = new DOMParser().parseFromString(responseText, 'text/html')
			// 			this.updateQuantityRules(this.dataset.section, html)
			// 		})
			// 		.catch((e) => console.error(e))
			// 		.finally(() => this.querySelector('[data-rules-cart]').classList.remove('is-loading'))
			// }
		}
	)
}

if (!customElements.get('l-product-installments')) {
	customElements.define(
		'l-product-installments',
		class LTAProductsInstallments extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Subscribe global event
				document.addEventListener('l:variant:changed', (event) => {
					const variantId = event.detail.variant.id

					const input = this.querySelector('input[name="id"]')
					input.value = variantId ?? ''
					input.dispatchEvent(new Event('change', { bubbles: true }))
				})
			}
		}
	)
}

/**********************************

* [PRODUCT BLOCK]: STICKY ATC

***********************************/
if (!customElements.get('l-product-sticky-atc')) {
	customElements.define(
		'l-product-sticky-atc',
		class LTAProductStickyATC extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.mainSubmitEl = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`)
				this.selectEl = this.querySelector('select')

				// Data
				this.variantsData = JSON.parse(this.querySelector('[data-sticky-atc-variants]').textContent)

				// Setup functions
				this.handleOnScroll()

				// Events
				this.addEventListener('click', this.handleOnClick.bind(this))

				document.addEventListener('l:cart:updated', (event) => {
					const submitATCEls = document.querySelectorAll('[data-sticky-atc-submit]')

					submitATCEls.forEach((submitATCEl) => {
						submitATCEl.classList.remove('is-loading')
					})
				})

				this.addEventListener('change', this.handleOnChange.bind(this))
				document.addEventListener('l:variant:changed', this.handleOnOptionsChange.bind(this))
			}

			handleOnClick(event) {
				const target = event.target

				if (target.tagName === 'BUTTON') {
					target.classList.add('is-loading')
				}
			}

			handleOnOptionsChange(event) {
				const { variant } = event.detail

				if (this.selectEl) {
					this.selectEl.value = variant.id
				}
			}

			handleOnChange(event) {
				const selectEl = event.target
				const selectedOptionEl = selectEl.selectedOptions[0]

				const selectedOptionValues = selectedOptionEl.dataset.optionIds.split(',')
				const variant = this.variantsData.find((variant) => variant.id == selectEl.value)

				this.mainSubmitEl.dispatchEvent(
					new CustomEvent('l:trigger-variant-change', {
						detail: {
							target: null,
							selectedOptionValues,
							variant,
						},
						bubbles: true,
					})
				)
			}

			handleOnScroll() {
				const checkStickyATC = () => {
					const rect = this.mainSubmitEl.getBoundingClientRect()
					const scrollHeight = document.documentElement.scrollHeight
					const screenHeight = window.innerHeight
					const scrollPosition = window.scrollY || document.documentElement.scrollTop

					/*
					 * Condition:
					 * 1. Main atc button is of out screen
					 * 2. Sticky atc button is not at the bottom of the page
					 */
					const isVisible =
						rect.bottom <= window.innerHeight &&
						rect.top < -100 &&
						scrollPosition + screenHeight + this.offsetHeight < scrollHeight

					this.classList.toggle('is-sticky', isVisible)
				}

				// Run default check
				checkStickyATC()

				// Subscribe scroll event
				document.addEventListener(
					'scroll',
					l_debounce(() => {
						checkStickyATC()
					}, 50)
				)
			}
		}
	)
}

/**********************************

* [PRODUCT BLOCK]: DELIVERY ESTIMATION

***********************************/

if (!customElements.get('l-product-delivery-estimation')) {
	customElements.define(
		'l-product-delivery-estimation',
		class LTADeliveryEstimation extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.minDays = parseInt(this.dataset.minDays)
				this.maxDays = parseInt(this.dataset.maxDays)

				if (!this.minDays || !this.maxDays) return

				this.minDeliveryDateEl = this.querySelector('[data-min-delivery-text]')
				this.maxDeliveryDateEl = this.querySelector('[data-max-delivery-text]')

				const today = new Date()

				if (this.minDeliveryDateEl) {
					this.minDeliveryDateEl.innerHTML = this.getNextWorkingDays(today, this.minDays)
				}

				if (this.maxDeliveryDateEl) {
					this.maxDeliveryDateEl.innerHTML = this.getNextWorkingDays(today, this.maxDays)
				}
			}

			getNextWorkingDays(date, daysToAdd) {
				let nextDay = new Date(date)
				let addedDays = 0

				while (addedDays < daysToAdd) {
					nextDay.setDate(nextDay.getDate() + 1)

					if (nextDay.getDay() !== 0 && nextDay.getDay() !== 6) {
						addedDays++
					}
				}

				return this.formatDate(nextDay)
			}

			formatDate(date) {
				const day = date.getDate()
				const month = date.toLocaleString('en-GB', { month: 'short' })

				return `${day} ${month}`
			}
		}
	)
}

/**********************************

* [PRODUCT BLOCK]: ADDED TO CART

***********************************/

if (!customElements.get('l-product-added-to-cart')) {
	customElements.define(
		'l-product-added-to-cart',
		class LTAProductAddedToCart extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.drawerEl = this.closest('l-drawer')
				this.variantEls = this.querySelectorAll('[data-variant-id]')
				this.viewCartBtnEl = this.querySelector('[data-view-cart]')

				// Events
				document.addEventListener('l:cart:updated', (event) => {
					const variant = event.detail.variant

					// Select the variant that is added to cart
					this.variantEls.forEach((variantEl) => {
						variantEl.classList.toggle('l-hidden', variantEl.dataset.variantId !== `${variant.id}`)
					})

					// Show the drawer
					this.drawerEl.drawer_open()
				})

				this.viewCartBtnEl.addEventListener('click', (event) => {
					event.preventDefault()

					this.drawerEl.drawer_close()

					const nativeCartDrawer = document.getElementById('MinimogCartDrawer')
					const nativeCartDrawerEnabled = window.MinimogSettings?.enable_cart_drawer === true

					if (nativeCartDrawerEnabled && typeof nativeCartDrawer?.open === 'function') {
						window.Rebuy?.SmartCart?.hide?.()
						requestAnimationFrame(() => nativeCartDrawer.open(this.viewCartBtnEl))
						return
					}

					if (!nativeCartDrawerEnabled && typeof window.Rebuy?.SmartCart?.show === 'function') {
						requestAnimationFrame(() => window.Rebuy.SmartCart.show())
						return
					}

					window.location.assign(this.viewCartBtnEl.href)
				})
			}
		}
	)
}

/**********************************

* [PRODUCT BLOCK]: INLINE TABS SPECIFICATIONS

***********************************/

if (!customElements.get('l-product-inline-tabs-specs')) {
	class ProductInlineTabsSpecs extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			this.tabEls = Array.from(this.querySelectorAll('[data-tab]'))
			this.contentEls = Array.from(this.querySelectorAll('[data-tab-content]'))

			this.tabEls.forEach((tabEl, index0) => {
				tabEl.addEventListener('click', () => {
					this.tabEls.forEach((_tabEl, _index0) => {
						_tabEl.classList.toggle('is-active', _index0 === index0)
					})

					this.contentEls.forEach((_contentEl, _index0) => {
						_contentEl.classList.toggle('l-hidden', _index0 !== index0)
					})
				})
			})
		}
	}

	customElements.define('l-product-inline-tabs-specs', ProductInlineTabsSpecs)
}

/*****************************************
 * [SECTION]: PRODUCT SPECIFICATIONS
 ******************************************/

class LTAPDPSpecifications extends HTMLElement {
	constructor() {
		super()
		this.handleTabClick = this.handleTabClick.bind(this)
	}

	connectedCallback() {
		this.triggerTabEls = this.querySelectorAll('[data-trigger-tab]')
		this.tabEls = this.querySelectorAll('[data-tab]')
		this.addEventListeners()
	}

	addEventListeners() {
		this.triggerTabEls.forEach((triggerTabEl) => {
			triggerTabEl.addEventListener('click', this.handleTabClick)
		})
	}

	handleTabClick(e) {
		e.preventDefault()
		const clickedTab = e.currentTarget

		this.triggerTabEls.forEach((el) => {
			el.classList.toggle('is-active', el === clickedTab)
		})

		this.tabEls.forEach((el) => {
			el.classList.toggle('l-hidden', clickedTab.dataset.triggerTab !== el.dataset.tab)
		})
	}
}

customElements.define('l-pdp-specifications', LTAPDPSpecifications)

/************************************************************************************

* MODULE: PRODUCT MEDIA GALLERY

*************************************************************************************/
if (!customElements.get('l-product-media-gallery')) {
	customElements.define(
		'l-product-media-gallery',
		class ProductMediaGallery extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				document.addEventListener('l:vendor:loaded', () => {
					this.setupCarousel()
				})

				document.addEventListener('l:variant:changed', (event) => {
					this.onVariantChange(event.detail)
				})
			}

			setupCarousel() {
				// Setup carousel for thumbnail first
				this.thumbnailCarousel = this.querySelector('[data-thumbnails-carousel]')
				this.thumbnailSlides = this.thumbnailCarousel.querySelectorAll('[data-media]')

				this.thumbnailSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.thumbnailCarouselSwiper = new window.Swiper(this.thumbnailCarousel, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 8,
					resistanceRatio: 0.2,
					mousewheel: true,
					navigation: {
						nextEl: this.thumbnailCarousel.querySelector('[data-next]'),
						prevEl: this.thumbnailCarousel.querySelector('[data-prev]'),
					},
					breakpoints: {
						1280: {
							direction: 'vertical',
						},
					},
				})

				// Setup carousel for main media
				this.mainCarousel = this.querySelector('[data-main-carousel]')
				this.mediaSlides = this.mainCarousel.querySelectorAll('[data-media]')
				this.navigationNext = this.mainCarousel.querySelector('[data-next]')
				this.navigationPrev = this.mainCarousel.querySelector('[data-prev]')

				this.mediaSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.mainCarouselSwiper = new window.Swiper(this.mainCarousel, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 0,
					autoHeight: true,
					mousewheel: true,
					keyboard: {
						enabled: true,
					},
					navigation: {
						nextEl: this.navigationNext,
						prevEl: this.navigationPrev,
					},
					pagination: {
						el: this.querySelector('[data-pagination]'),
						clickable: true,
						dynamicBullets: true,
						dynamicMainBullets: 4,
					},
					thumbs: {
						swiper: this.thumbnailCarouselSwiper,
					},
				})

				// Setup controller
				this.mainCarouselSwiper.controller.control = this.thumbnailCarouselSwiper

				// Setup changed events
				this.handleSlideChangeEvent()
				this.updateVerticalThumbnailCarouselHeight()
			}

			handleSlideChangeEvent() {
				this.mainCarouselSwiper.on('slideChange', (swiper) => {
					const activeSlideEl = this.mediaSlides[swiper.activeIndex]

					l_pauseAllMedia(this)
					l_playMedia(activeSlideEl)

					this.updateVerticalThumbnailCarouselHeight()
				})
			}

			onVariantChange(data) {
				const selectedVariant = data.variant
				const matchMediaId = selectedVariant?.featured_media?.id

				if (!matchMediaId) return

				this.mediaSlides.forEach((mediaSlide, index0) => {
					const mediaId = mediaSlide.dataset.mediaId

					if (mediaId == matchMediaId) {
						this.mainCarouselSwiper.slideTo(index0, true)
					}
				})
			}

			updateVerticalThumbnailCarouselHeight() {
				this.style.setProperty('--l-thumbnail-carousel-max-height', `${this.mainCarousel.offsetHeight}px`)
			}
		}
	)
}

/************************************************************************************

* MODULE: PRODUCT MEDIA GALLERY MODAL

*************************************************************************************/

if (!customElements.get('l-product-modal-gallery')) {
	customElements.define(
		'l-product-modal-gallery',
		class LTAProductModalGallery extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.closeButtonEls = this.querySelectorAll('[data-close]')
				this.cardEl = this.querySelector('[data-card]')
				this.mediaEls = this.querySelectorAll('[data-media]')

				// Events
				document.addEventListener('l:vendor:loaded', () => {
					this.setupCarousel()
				})

				this.closeButtonEls.forEach((closeButtonEl) => {
					closeButtonEl.addEventListener('click', (event) => {
						this.close()
					})
				})

				document.addEventListener('media:click', (event) => {
					// Open the active media
					const mediaId = event.detail.mediaId
					this.mediaEls.forEach((mediaEl, index0) => {
						const _mediaId = mediaEl.getAttribute('data-media-id')
						if (_mediaId == mediaId) {
							this.mainCarouselSwiper.slideTo(index0)
						}
					})

					// Open the modal
					this.open()
				})

				this.addEventListener('click', this.outSideClick.bind(this))
			}

			setupCarousel() {
				// Setup carousel for thumbnail first
				this.thumbnailCarousel = this.querySelector('[data-thumbnails-carousel]')
				this.thumbnailSlides = this.thumbnailCarousel.querySelectorAll('[data-media]')

				this.thumbnailSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.thumbnailCarouselSwiper = new window.Swiper(this.thumbnailCarousel, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 8,
					resistanceRatio: 0.2,
					navigation: {
						nextEl: this.thumbnailCarousel.querySelector('[data-next]'),
						prevEl: this.thumbnailCarousel.querySelector('[data-prev]'),
					},
				})

				// Setup carousel for main media
				this.mainCarousel = this.querySelector('[data-main-carousel]')
				this.mediaSlides = this.mainCarousel.querySelectorAll('[data-media]')
				this.navigationNext = this.mainCarousel.querySelector('[data-next]')
				this.navigationPrev = this.mainCarousel.querySelector('[data-prev]')

				this.mediaSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.mainCarouselSwiper = new window.Swiper(this.mainCarousel, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 0,
					autoHeight: true,
					keyboard: {
						enabled: true,
					},
					navigation: {
						nextEl: this.navigationNext,
						prevEl: this.navigationPrev,
					},
					thumbs: {
						swiper: this.thumbnailCarouselSwiper,
					},
				})

				// Setup controller
				this.mainCarouselSwiper.controller.control = this.thumbnailCarouselSwiper

				// Setup changed events
				this.handleSlideChangeEvent()
			}

			handleSlideChangeEvent() {
				this.mainCarouselSwiper.on('slideChange', (swiper) => {
					const activeSlideEl = swiper.slides[swiper.activeIndex]

					l_pauseAllMedia(this)
					l_playMedia(activeSlideEl)
				})
			}

			open() {
				l_lockBodyScroll()
				l_updateHeaderCSSVariables()
				this.classList.add('is-active')
			}

			close() {
				l_unLockBodyScroll()
				this.classList.remove('is-active')
				l_pauseAllMedia(this)
			}

			outSideClick(event) {
				if (!this.cardEl.contains(event.target) && this.classList.contains('is-active')) {
					this.close()
				}
			}
		}
	)
}

if (!customElements.get('l-media-zoom')) {
	customElements.define(
		'l-media-zoom',
		class LTAMediaZoom extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				document.addEventListener('zoomifyJs:loaded', () => {
					this.setupZoomMedia()
				})
			}

			setupZoomMedia() {
				const zoomify = new ZoomifyJs({
					selector: this.querySelector('.js-zoomifyJs-image'),
					transitionDuration: 300,
					easing: 'ease-in-out',
					scale: 2,
					clickToZoom: true,
				})
			}
		}
	)
}

if (!customElements.get('l-product-media-single')) {
	customElements.define(
		'l-product-media-single',
		class LTAMediaSingle extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.addEventListener('click', (event) => {
					this.dispatchEvent(
						new CustomEvent('media:click', { bubbles: true, detail: { mediaId: this.dataset.mediaId } })
					)
				})
			}
		}
	)
}

/**********************************

* [SECTION]: RELATED PRODUCTS

***********************************/

if (!customElements.get('l-product-recommendations')) {
	customElements.define(
		'l-product-recommendations',
		class LTAProductRecommendations extends HTMLElement {
			observer = undefined

			constructor() {
				super()
			}

			connectedCallback() {
				this.initializeRecommendations()
			}

			initializeRecommendations() {
				this.observer?.unobserve(this)
				this.observer = new IntersectionObserver(
					(entries, observer) => {
						if (!entries[0].isIntersecting) return
						observer.unobserve(this)
						this.loadRecommendations()
					},
					{ rootMargin: '0px 0px 400px 0px' }
				)
				this.observer.observe(this)
			}

			loadRecommendations() {
				fetch(this.dataset.url)
					.then((response) => response.text())
					.then((text) => {
						const html = document.createElement('div')
						html.innerHTML = text
						const recommendations = html.querySelector('l-product-recommendations')

						if (recommendations?.innerHTML.trim().length) {
							this.innerHTML = recommendations.innerHTML
						}
					})
					.catch((e) => {
						console.error(e)
					})
			}
		}
	)
}

